<template>
  <PremiumMetricWidgetBase :widget="widget" @metric-loaded="handleMetricLoaded" :state="state">
    <div v-if="paths?.length === 0" class="widget__body-empty">
      <IconInfo />
      <p>No path data available for this event</p>
    </div>

    <div v-if="paths?.length === 1" class="widget__body-empty">
      <IconInfo />
      <p>No paths are shown for a single zone event</p>
    </div>

    <span v-else>
      <div class="widget-path__container" v-for="(path, index) in displayedPaths" :key="index">
        <div class="widget-path__title" :style="{ width: getTitleWidth }">
          <p>{{ getOrdinalSuffix(index + 1) }} Most Common Path</p>
        </div>
        <div
          class="widget-path__item-container"
          v-for="path_item in path.path"
          :key="path_item"
          :style="{ width: getItemWidth }"
        >
          <div
            class="widget-path__item"
            :style="{ backgroundColor: getPathItemColor(path_item) }"
            :title="path_item"
          >
            <p class="widget-path__item-text">
              {{ path_item }}
            </p>
          </div>
          <Arrow class="widget-path__arrow" />
        </div>
      </div>
    </span>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import Arrow from './Arrow.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import { getOrdinalSuffix } from '@/utils/ordinalSuffix';
import { barColors } from '@/utils/chartColors';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const MAX_DISPLAYED_PATHS = 3;

const props = withDefaults(
  defineProps<{
    widget: WidgetObject;
    area?: WidgetFilter;
    zone?: WidgetSetting;
    maxPathItems?: number;
  }>(),
  {
    maxPathItems: 5,
  },
);

const metricData = ref<any>(null);

// Map to store color assignments for path items
const pathItemColors = ref<Map<string, string>>(new Map());

// Function to get consistent color for a path item
const getPathItemColor = (pathItem: string): string => {
  if (!pathItemColors.value.has(pathItem)) {
    // Assign a color based on the current number of assigned colors
    const colorIndex = pathItemColors.value.size % barColors.length;
    pathItemColors.value.set(pathItem, barColors[colorIndex]);
  }
  return pathItemColors.value.get(pathItem) || barColors[0];
};

const handleMetricLoaded = (metric: any) => {
  metricData.value = metric;
};

const paths = computed(() => {
  const temp = metricData.value?.data?.data?.paths || [];
  const maxItems = props.maxPathItems || 4; // Default to 4 if not specified

  // Create a deep copy to avoid mutating original data
  const pathsCopy = temp.map((path: any) => ({
    ...path,
    path: [path.start, ...path.path], // Create a new array for the path
  }));

  for (let i = 0; i < pathsCopy.length; i++) {
    if (pathsCopy[i].path.length > maxItems) {
      pathsCopy[i].path = pathsCopy[i].path.slice(0, maxItems);
    }
  }
  return pathsCopy;
});

const displayedPaths = computed(() => {
  return paths.value.slice(0, MAX_DISPLAYED_PATHS);
});

const state = computed(() => {
  if (paths.value?.length === 1) {
    return 'single-zone-disabled';
  }
  return null;
});

// Calculate dynamic widths based on number of items
const getItemWidth = computed(() => {
  const maxItems = props.maxPathItems;
  // Each item gets equal width, including the title section
  return `${100 / (maxItems + 1)}%`; // +1 for the title section
});

const getTitleWidth = computed(() => {
  const maxItems = props.maxPathItems;
  return `${100 / (maxItems + 1)}%`; // Same as item width
});
</script>

<style scoped lang="scss">
.widget-path__container {
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 30%;
  margin-bottom: variables.$sizing-xs;
}

.widget-path__title {
  p {
    margin: auto auto;
  }
}

.widget-path__item-text {
  @include typography.font-body($size: sm, $weight: normal);
  color: variables.$primitives-color-background-100;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.widget-path__item-container {
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 100%;
}

.widget-path__item-container:last-child {
  .widget-path__arrow {
    opacity: 0;
  }
}

.widget-path__item {
  align-items: center;
  border-radius: variables.$sizing-xs;
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  padding: 0 variables.$sizing-xs;
}
</style>
